<template>
  <div>
    <breadcrumb :crumbs="crumbs"></breadcrumb>
    <authorized-link tag="create_customer">
      <router-link v-if='!selectCustomer' slot="button" :to="{ name: 'new_customer', params: { createByContract: true } }" class="ls-btn ls-btn-primary ls-xs-margin-top"><i class="fa fa-user-plus" aria-hidden="true"></i><span class="ls-xs-margin-left">{{ $t('customers.new.new_customer') }}</span></router-link>
    </authorized-link>
    <form @keypress.enter.prevent @submit.prevent="validateBeforeSubmit()" class="ls-form">
      <div class="row">
        <div class="col-md-5 col-lg-6 col-sm-12 col-xs-12 ls-no-padding">
          <p>{{ $t('contracts.new.new_contract_message') }}</p>
          <fieldset>
            <label class="ls-label" :class="{ 'ls-error': errors.has($t('contracts.customer'))}">
              <b class="ls-label-text">{{ $t('contracts.new.select_customer') }}</b>
              <v-select
                @input.native="clearFilter"
                @search="onSearchCustomer"
                label="name"
                id="customerSearch"
                transition="none"
                v-model="selectCustomer"
                v-validate="'required'"
                :data-vv-name="$t('contracts.customer')"
                :placeholder="$t('contracts.placeholder.search_by_customer_name')"
                :options="customerOptions"
                :value.sync="selectCustomer">
                <template slot="no-options">{{ $t('contracts.new.find_customer') }}</template>
              </v-select>
              <small class="ls-help-message" v-show="errors.has($t('contracts.customer'))">{{ errors.first($t('contracts.customer')) }}</small>
            </label>
          </fieldset>
          <authorized-link tag='create_customer'>
            <div v-if="!selectCustomer">
              <router-link slot="button" :to="{ name: 'new_customer', params: { createByContract: true } }" class="ls-btn ls-btn-primary ls-xs-margin-bottom ls-xs-margin-top"><i class="fa fa-user-plus" aria-hidden="true"></i><span class="ls-xs-margin-left">{{ $t('customers.new.new_customer') }}</span></router-link>
            </div>
          </authorized-link>
        </div>
        <div v-if="selectCustomer" class="col-md-7 col-lg-6 col-sm-12 col-xs-12">
          <label class="ls-label">
            <b class="ls-label-text">{{ $t('contracts.new.customer_data') }}</b>
            <div class="d-flex flex-wrap ls-box-filter">
              <div class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.email') }}: <span class="span-text">{{ selectCustomer.email }}</span> </b>
                </label>
              </div>
              <div class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.name') }}: <span class="span-text">{{ selectCustomer.name }}</span></b>
                </label>
              </div>
              <div v-if="selectCustomer.cpf" class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.cpf') }}: <span class="span-text">{{ this.formatCPF(selectCustomer.cpf) }}</span></b>
                </label>
              </div>
              <div v-if="selectCustomer.cnpj" class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.cnpj') }}: <span class="span-text">{{ this.formatCNPJ(selectCustomer.cnpj) }}</span></b>
                </label>
              </div>
              <div v-if="selectCustomer.cnpj" class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text">{{ $t('customers.attributes.cnpj') }}: <span class="span-text">{{ this.selectCustomer.trade_name }}</span></b>
                </label>
              </div>
              <div v-if="selectCustomer.kind === 'P'" class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text">{{ $t('customers.attributes.type') }}: <span class="span-text">{{ $t('contracts.new.people') }}</span></b>
                </label>
              </div>
              <div v-else class="col-md-6 col-sm-12 col-xs-12">
                <label class="ls-label d-flex align-items-center">
                  <b class="ls-label-text">{{ $t('customers.attributes.type') }}: <span class="span-text">{{ $t('contracts.new.enterprise') }}</span></b>
                </label>
              </div>
              <div class="ls-no-padding col-md-12 col-sm-12 col-xs-12" v-if="selectCustomer.address">
                <div v-if="selectCustomer.address.postal_code" class="col-md-6 col-sm-12 col-xs-12">
                  <label class="ls-label d-flex align-items-center">
                    <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.address.postal_code') }}: <span class="span-text">{{ this.formatCep(selectCustomer.address.postal_code) }}</span></b>
                  </label>
                </div>
                <div v-if="selectCustomer.address.street" class="col-md-6 col-sm-12 col-xs-12">
                  <label class="ls-label d-flex align-items-center">
                    <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.address.street') }}: <span class="span-text">{{ selectCustomer.address.street }}</span></b>
                  </label>
                </div>
                <div v-if="selectCustomer.address.number" class="col-md-6 col-sm-12 col-xs-12">
                  <label class="ls-label d-flex align-items-center">
                    <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.address.number') }}: <span class="span-text">{{ selectCustomer.address.number }}</span></b>
                  </label>
                </div>
                <div v-if="selectCustomer.address.neighborhood" class="col-md-6 col-sm-6 col-xs-6">
                  <label class="ls-label d-flex align-items-center">
                    <b class="ls-label-text ls-sm-margin-bottom">{{ $t('customers.attributes.address.neighborhood') }}: <span class="span-text">{{ selectCustomer.address.neighborhood }}</span></b>
                  </label>
                </div>
              </div>
              <div class="col-md-offset-8 col-md-4 col-lg-offset-9 col-lg-3 col-xs-offset-8 col-sm-offset-6 col-xs-4 col-sm-6 ls-float-right">
                <router-link slot="button" :to="{ name: 'edit_customer', params: { id: selectCustomer.value || selectCustomer.id, updateByContract: true }}" class="ls-btn ls-no-margin ls-float-right">
                  <i class="fa fa-pencil-square-o" aria-hidden="true"></i>
                  <span>{{ $t('customers.main.edit') }}</span>
                </router-link>
              </div>
            </div>
          </label>
        </div>
      </div>

      <div class="row">
        <div class="ls-float-right col-md-12 col-xs-12 col-sm-12 ls-no-padding-left">
          <div data-ls-module="collapse" :class="[ 'ls-collapse',  showCollapse ? 'ls-collapse-opened' : '' ]">
            <div @click="toogleCollapse()" class="ls-collapse-header">
              <h4 class="ls-collapse-title">{{ $t('contracts.labels.contract_settings') }}</h4>
            </div>
            <div class="ls-collapse-body" id="30">
              <div class="row d-flex flex-wrap">
                <div class="col-xlg-2 col-xs-12 col-lg-3 ls-no-padding-right">
                  <label class="ls-label">
                    <b class="ls-label-text ls-xs-margin-bottom">{{ $t('contracts.attributes.settings.days_to_overdue_invoice') }}
                      <popover :position="'right'">
                        <div slot="content">{{ $t('contracts.new.popover.days_to_overdue_invoice') }}</div>
                      </popover>
                    </b>
                    <div class="col-xlg-3 col-lg-3 col-xs-2 ls-no-padding">
                      <input type="number"
                      class="ls-field-sm"
                      min=0
                      v-validate="'required'"
                      v-model='settings.days_to_overdue_invoice.value'>
                    </div>
                  </label>
                </div>
                <div class="col-xlg-2 col-lg-3 col-xs-12">
                  <label class="ls-label">
                    <b class="ls-label-text ls-xs-margin-bottom">{{ $t('contracts.attributes.url_notification') }}</b>
                    <input type="text"
                    class="ls-field-sm"
                    :placeholder="$t('contracts.placeholder.url_notification')"
                    v-model='urlNotification'>
                  </label>
                </div>

                <div class="col-xlg-2 col-lg-3 col-xs-12 ls-no-padding ls-display-flex align-items-center">
                  <label class="ls-label col-xlg-12 ls-display-flex align-itens-end ls-no-margin ls-sm-margin-left">
                    <input
                      type="checkbox"
                      name="settings.generate_service_tax_invoices.value"
                      class="ls-float-left"
                      v-model="settings.generate_service_tax_invoices.value">
                    <p class="ls-display-flex ls-no-margin"><span class="txt-check">{{ $t('contracts.attributes.settings.generate_service_tax_invoices')}}</span></p>
                  </label>
                </div>

                <div class="col-xlg-2 col-lg-3 col-xs-12 ls-no-padding ls-display-flex align-items-center">
                  <label class="ls-label col-xlg-12 ls-display-flex align-itens-end ls-no-margin ls-sm-margin-left">
                    <input
                      type="checkbox"
                      name="postPaid"
                      class="ls-float-left"
                      v-model="postPaid">
                    <p class="ls-display-flex ls-no-margin"><span class="txt-check">{{ $t('contracts.attributes.post_paid')}}</span></p>
                  </label>
                </div>

                <div class="col-xlg-3 col-lg-offset-9 col-lg-3 col-xs-12 justify-content-flex-end ls-display-flex align-itens-center ls-xs-margin-top-in-sm">
                  <authorized-link tag="create_contract_tax">
                    <button type="button" @click.prevent="openTaxesModal" class="ls-btn ls-btn-primary ls-no-margin">
                      <i class="fa fa-usd" aria-hidden="true"></i>
                      <span>{{ $t('contracts.new.include_taxes') }}</span>
                    </button>
                  </authorized-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row" v-if="selectPlan.length > 0">
        <div class="col-md-12 col-sm-12 col-xs-12 ls-display-flex justify-content-flex-end">
          <button type="button" class="ls-btn ls-btn-primary ls-no-margin" @click.prevent="addPlan">
            <i class="fa fa-plus" aria-hidden="true"></i>
            <span>{{ $t('application.button.add_plan') }}</span>
          </button>
        </div>
      </div>
      <div class="row" v-for='index in plansQuantity'>
        <div class="col-sm-12 col-xs-12 col-md-12 ls-box-filter">
          <fieldset>
            <div :data-vv-scope="index">
              <label class="ls-label" :class="{ 'ls-error': errors.has(index.toString() + '.' + $t('contracts.plan'))}">
                <b class="ls-label-text">{{ $t('contracts.new.select_plan') }}</b>
                <v-select
                  @input.native="clearFilter"
                  @search="onSearchPlan"
                  closeOnSelect="true"
                  label="name"
                  class="ls-sm-margin-bottom"
                  transition="none"
                  v-bind:ref="index"
                  v-model="selectPlan[(index - 1)]"
                  v-validate="'required'"
                  :data-vv-scope="index"
                  :placeholder="$t('contracts.placeholder.search_by_plan_name')"
                  :options="plansOptions"
                  :value.sync="selectPlan[(index - 1)]">
                  <template slot="no-options">{{ $t('contracts.new.find_plan') }}</template>
                </v-select>
                <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('contracts.plan'))">{{ errors.first(index.toString() + '.' + $t('contracts.plan')) }}</small>
              </label>
            </div>
            <div class="row">
              <div v-if="(index - 1)  !== 0" class="col-md-12 col-sm-12 col-xs-12">
                <button type="button"  class="ls-btn ls-btn-danger" @click.prevent="removePlan(index - 1)"><i class="fa fa-trash-o" aria-hidden="true"></i>&nbsp;&nbsp;{{ $t('application.button.remove_plan') }}</button>
              </div>
            </div>
            <div class="container-fluid" v-if="selectPlan[(index - 1)]">
              <div class="row">
                <div class="flex-wrap">
                  <div class="col-xlg-1 ls-no-padding col-lg-1-5 col-sm-12 col-xs-6">
                    <label class="ls-label" :class="{ 'ls-error': errors.has(index.toString() + '.' + $t('plans.attributes.periodicity')) }">
                      <span class="ls-label-text col-xs-12 ls-sm-margin-bottom ls-no-padding">
                        {{ $t('contracts.new.periodicity') }}
                        <popover :position="'right'">
                          <div slot="content">{{ $t('contracts.labels.periodicity') }}</div>
                        </popover>
                      </span>
                      <div class="col-xlg-5 col-xs-4 col-lg-5 ls-no-padding">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          required
                          name="plans.attributes.periodicity"
                          v-model="selectPlan[(index - 1)].periodicity"
                          v-validate="'required|decimal:0|min_value:1'"
                          :name="$t('plans.attributes.periodicity')"
                          :data-vv-scope="index">
                      </div>
                      <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('plans.attributes.periodicity'))">{{ errors.first(index.toString() + '.' + $t('plans.attributes.periodicity')) }}</small>
                    </label>
                  </div>

                  <div class="col-md-4 col-xlg-1 col-lg-1-5 col-sm-12 col-xs-6">
                    <label class="ls-label" :class="{ 'ls-error': errors.has(index.toString() + '.' + $t('plans.attributes.quantity')) }">
                     <span class="ls-label-text col-xs-12 ls-sm-margin-bottom ls-no-padding">
                        {{ $t('contracts.new.quantity') }}
                        <popover :position="'right'">
                          <div slot="content">{{ $t('contracts.labels.quantity') }}</div>
                        </popover>
                      </span>
                      <div class="col-xlg-7 col-lg-6 col-xs-5 ls-no-padding">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          required
                          name="plans.attributes.quantity"
                          v-model="selectPlan[(index - 1)].quantity"
                          v-validate="'required|decimal:0|max_quantity'"
                          :data-vv-scope="index"
                          :name="$t('plans.attributes.quantity')">
                      </div>
                      <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('plans.attributes.quantity'))">{{ errors.first(index.toString() + '.' + $t('plans.attributes.quantity')) }}</small>
                    </label>
                  </div>

                  <div class="col-md-4 col-xlg-2 col-lg-2 col-sm-12 col-xs-8 ls-no-padding-left">
                    <label class="ls-label" :class="{ 'ls-error': errors.has(index.toString() + '.' + $t('plans.attributes.price')) }">
                      <b class="ls-label-text ls-sm-margin-bottom">{{ $t('plans.new.value') }}</b>
                      <div class="col-xlg-8">
                        <money-input
                          v-model="selectPlan[(index - 1)].price"
                          v-validate="'required|onlyPositive'"
                          :value="selectPlan[(index - 1)].price"
                          @updateValue="(value) => { selectPlan[(index - 1)].price = value }"
                          required
                          step="2"
                          :name="$t('plans.attributes.price')"
                          :data-vv-scope="index">
                        </money-input>
                      </div>
                      <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('plans.attributes.price'))">{{ errors.first(index.toString() + '.' + $t('plans.attributes.price')) }}</small>
                    </label>
                  </div>


                  <authorized-link tag="create_contract_plan_entry">
                    <div class="col-md-4 col-xlg-1 col-lg-1-5 col-sm-12 col-xs-6 ls-no-padding">
                      <label class="ls-label" :class="{'ls-error': errors.has(index.toString() + '.' + $t('contracts.new.discount_quantity')) }">
                        <span class="ls-label-text ls-sm-margin-bottom">{{ $t('contracts.new.discount_quantity')}}
                          <popover :position="'right'">
                            <div slot="content">{{ $t('contracts.labels.discount_quantity') }}</div>
                          </popover>
                        </span>
                        <div class="col-xlg-6 col-xs-4 col-lg-5 ls-no-padding">
                          <input
                            type="number"
                            v-model="selectPlan[(index - 1)].contract_plan_entries[0].quantity"
                            required
                            min="0"
                            v-validate="'required|decimal:0|max_quantity'"
                            :name="$t('contracts.new.discount_quantity')"
                            :data-vv-scope="index"
                            :placeholder="$t('contracts.new.discount_quantity')" />
                        </div>
                        <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('contracts.new.discount_quantity'))">{{ errors.first(index.toString() + '.' + $t('contracts.new.discount_quantity')) }}</small>
                      </label>
                    </div>
                    <div class="col-md-4 col-xlg-2 col-lg-2 col-sm-12 col-xs-6">
                      <label class="ls-label" :class="{ 'ls-error': errors.has(index.toString() + '.' + $t('contracts.new.discount_price')) }">
                        <b class="ls-label-text ls-sm-margin-bottom">{{ $t('contracts.new.discount_price') }}</b>
                        <div class="col-xlg-8">
                          <money-input
                            v-model="selectPlan[(index - 1)].contract_plan_entries[0].price"
                            v-validate="'required|onlyPositive'"
                            :value="selectPlan[(index - 1)].contract_plan_entries[0].price"
                            @updateValue="(value) => { selectPlan[(index - 1)].contract_plan_entries[0].price = value }"
                            :data-vv-scope="index"
                            :name="$t('contracts.new.discount_price')">
                          </money-input>
                        </div>
                        <small class="ls-help-message" v-show="errors.has(index.toString() + '.' + $t('contracts.new.discount_price'))">{{ errors.first(index.toString() + '.' + $t('contracts.new.discount_price')) }}</small>
                      </label>
                    </div>
                  </authorized-link>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 ls-no-padding">
          <button class="ls-btn-primary ls-float-right" type="submit"><i class="fa fa-floppy-o" aria-hidden="true"></i>&nbsp;&nbsp;{{ $t('contracts.buttons.new_contract') }}</button>
        </div>
      </div>
    </form>
    <modal-taxes v-model="modalTaxes" :linkedTaxes="selectedTaxes" :unlinkedTaxes="unselectedTaxes" :save="false"></modal-taxes>
  </div>
</template>
<script>
  import { mapActions, mapMutations, mapGetters } from 'vuex'
  import Breadcrumb from 'src/app/components/shareds/Breadcrumb'
  import { setFilteredPlans, clearFilteredPlans } from 'src/app/pages/contracts/vuex/mutations-types'
  import { setFilterCustomer, clearFilterCustomer } from 'src/app/pages/customers/vuex/mutations-types'
  import MoneyInput from 'src/app/components/shareds/money-input/MoneyInput'
  import scroll from 'src/utils/scroll'
  import ModalTaxes from './components/ModalTaxes'
  import filter from 'src/utils/filter'
  import Popover from 'src/app/components/shareds/popover/Popover'
  import money from 'src/utils/money'

  export default {
    watch: {
      plansQuantity: function () {
        this.scrollPage()
      }
    },
    name: 'new-contract',
    components: { Breadcrumb, MoneyInput, Popover, ModalTaxes },
    data () {
      return {
        money: money,
        modalTaxes: { show: false },
        selectedTaxes: [],
        unselectedTaxes: [],
        selectCustomer: null,
        showCollapse: true,
        postPaid: false,
        plansQuantity: 1,
        urlNotification: '',
        settings: {
          days_to_overdue_invoice: { value: 0 },
          generate_service_tax_invoices: { value: false }
        },
        customerOptions: [],
        plansOptions: [],
        selectPlan: [],
        crumbs: {
          'ico': 'ls-ico-edit-admin',
          'section': this.$t('contracts.new.create'),
          'paths': {
            0: {
              'title': this.$t('contracts.new.subscriptions'),
              'path': '/contracts/new'
            },
            1: {
              'title': this.$t('contracts.new.create'),
              'path': ''
            }
          }
        }
      }
    },
    mounted () {
      if (this.$route.params.customer) this.selectCustomer = this.$route.params.customer
    },
    computed: {
      ...mapGetters(['customers', 'plans', 'taxes', 'filterCustomers', 'filteredPlans'])
    },
    methods: {
      ...mapActions(['loadCustomers', 'loadPlans', 'loadTaxes', 'createContract', 'createInvoice', 'editContractSetting', 'createContractTax']),
      ...mapMutations(['clearFlash', 'addFlash']),
      validateBeforeSubmit () {
        this.$loader.show()
        this.$validator.validateScopes().then(this.afterValidate)
      },
      onSearchCustomer (search, loading) {
        let self = this
        loading(true)
        setTimeout(function () {
          self.getCustomers(search)
          loading(false)
        }, 350)
      },
      onSearchPlan (search, loading) {
        let self = this
        loading(true)
        setTimeout(function () {
          self.getPlans(search)
          loading(false)
        }, 350)
      },
      clearFilter () {
        this.customerOptions = []
        this.plansOptions = []
      },
      afterValidate (response) {
        if (response) {
          let taxIds = []
          let settingsArray = []
          for (let tax of this.selectedTaxes) {
            taxIds.push({ id: tax.id })
          }
          for (let setting in this.settings) {
            settingsArray.push({ setting_tag: setting, value: this.settings[setting].value })
          }
          let params = {
            customer_id: this.selectCustomer.value || this.selectCustomer.id,
            seller_id: 1,
            seller_code: JSON.parse(window.localStorage.seller).code,
            contract: { day_expiration: this.currentDay(),
              url_notification: this.urlNotification,
              post_paid: this.postPaid,
              plans: this.selectPlan,
              taxes: taxIds,
              settings: settingsArray }
          }
          this.createContract(params).then(this.afterCreateContract)
        } else {
          this.$loader.hide()
          let self = this
          setTimeout(function () {
            self.clearFlash()
          }, 2000)
        }
      },
      afterCreateContract (response) {
        this.$router.push('/contracts')
      },
      addPlan () {
        this.plansOptions = []
        this.plansQuantity += 1
        let self = this
        setTimeout(function () {
          self.scrollPage()
          self.$refs[self.plansQuantity][0].$el.getElementsByClassName('form-control')[0].focus()
        }, 100)
      },
      scrollPage () {
        window.scrollTo(0, document.body.scrollHeight)
      },
      removePlan (index) {
        this.selectPlan.splice(index, 1)
        this.plansQuantity -= 1
      },
      getPlans (params) {
        this.filterPlansQuery('plan[name]', params)
        let parameters = filter.toQuery(this.filteredPlans)
        this.loadPlans(parameters).then(this.afterPlansLoad)
      },
      afterPlansLoad () {
        this.$loader.hide()
        this.plansOptions = this.getPlansIds()
      },
      toogleCollapse () {
        this.$helper.$emit('closeCollapses', this.type)
        this.showCollapse = !this.showCollapse
      },
      closeCollapse (type) {
        if (this.type !== type) {
          this.showCollapse = false
        }
      },
      getPlansIds () {
        let plans = []
        for (let plan of this.plans) {
          plans.push({
            code: plan.code,
            name: plan.name,
            price: plan.price,
            periodicity: plan.periodicity,
            quantity: plan.quantity || 0,
            contract_plan_entries: [{
              quantity: 0,
              price: 0.00
            }]
          })
        }
        return plans
      },
      getCustomers (params) {
        this.filterCustomerQuery('customer[name]', params)
        let parameters = filter.toQuery(this.filterCustomers)
        this.loadCustomers(parameters).then(this.afterCustomersLoad)
      },
      filterCustomerQuery (key, value) {
        if (value !== '' && value !== undefined) {
          this.$store.commit(setFilterCustomer, { key: key, value: value })
          this.$store.commit(setFilterCustomer, { key: 'customer[active]', value: true })
        } else {
          this.$store.commit(clearFilterCustomer)
        }
      },
      filterPlansQuery (key, value) {
        if (value !== '' && value !== undefined) {
          this.$store.commit(setFilteredPlans, { key: key, value: value })
          this.$store.commit(setFilteredPlans, { key: 'plan[active]', value: true })
        } else {
          this.$store.commit(clearFilteredPlans)
        }
      },
      afterCustomersLoad () {
        this.$loader.hide()
        this.customerOptions = this.customers
      },
      currentDay () {
        return new Date().getDate()
      },
      formatCep (cep) {
        let regex = /^([\d]{2})\.*([\d]{3})-*([\d]{3})/
        return cep.replace(regex, '$1.$2-$3')
      },
      formatCPF (cpf) {
        let regex = /^([\d]{3})\.*([\d]{3})\.*([\d]{3})-*([\d]{2})/
        return cpf.replace(regex, '$1.$2.$3-$4')
      },
      formatCNPJ (cnpj) {
        let regex = /^([\d]{2})\.*([\d]{3})\.*([\d]{3})-*([\d]{4}).*([\d]{2})/
        return cnpj.replace(regex, '$1.$2.$3/$4-$5')
      },
      // Métodos da modal de impostos
      openTaxesModal () {
        let params = 'tax[active]=true'
        this.loadTaxes(params).then(this.showTaxesModal)
      },
      showTaxesModal () {
        this.getUnselectedTaxes()
        this.modalTaxes.show = true
        this.modalTaxes.callback = scroll.scrollToFlash
      },
      getUnselectedTaxes () {
        if (this.unselectedTaxes.length || this.selectedTaxes.length) return
        this.unselectedTaxes = this.taxes
      }
    }
  }
</script>
<style>
  .d-flex {
    display: flex;
  }
  .align-self-center {
    align-self: center;
  }
  .justify-content-flex-end {
    justify-content: flex-end;
  }
  .flex-wrap {
    flex-wrap: wrap;
  }
  .align-items-center {
    align-items: center;
  }
  .span-text {
    color: #888888;
    font-size: 12px;
  }
</style>



// WEBPACK FOOTER //
// New.vue?9d718466